<template>
  <v-combobox
      v-model="localModel"
      :label="schema['label']"
      :rules="localRules"
      :class="schema['x-class']"
      v-bind="localProps"
      v-on="$listeners"
      :items="returnLocalItems"
      :item-text="schema['x-itemTitle']"
      :item-value="schema['x-itemKey']"
      :loading="loading"
      :search-input.sync="search"
      :disabled="disabled"
  >
    <template v-for="(_, slot) in $scopedSlots" v-slot:[slot]="scope">
      <slot :name="slot" v-bind="scope"/>
    </template>
    <template v-slot:append-item>
      <div v-if="lazy && isNextPage">
        <v-list-item class="pa-0 d-flex justify-center" v-intersect="loadMore">
          <div v-show="appending">
            <v-progress-circular
                indeterminate
                color="primary"
            ></v-progress-circular>
          </div>
        </v-list-item>
      </div>
    </template>
    <template #append>
      <slot name="append"/>
    </template>

  </v-combobox>
</template>

<script>
import _ from "lodash";
import {mapActions, mapGetters} from "vuex";
import exprEvalParser from "@koumoul/vjsf/lib/utils/expr-eval-parser";
import {isValidUrl, Iterator} from "../shared/shared";

const matchAll = require('match-all')

class Pagination {
  constructor(itemPerPage = 20) {
    this._state = {
      page: 0,
      totalElements: 0,
      itemsPerPage: itemPerPage,
    };
  }

  nextPage() {
    if (this.isNextPage()) this._state.page++;
  }

  isNextPage() {
    return (
        this._state.page + 1 <
        Math.ceil(this._state.totalElements / this._state.itemsPerPage)
    );
  }

  setTotalElements(val) {
    this._state.totalElements = val;
  }

  resetPage() {
    this._state.page = 0;
  }

  getPage() {
    return this._state.page;
  }

  getTotalElements() {
    return this._state.totalElements
  }

  getItemsPerPage() {
    return this._state.itemsPerPage;
  }
}

export default {
  name: "TcnLazyCombobox",
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {type: Object, default: null},
    options: {type: Object, required: true},
    schema: {type: Object, required: true},
    fullSchema: {type: Object, required: true},
    fullKey: {type: String, required: true},
    label: {type: String, default: ''},
    htmlDescription: {type: String, default: ''},
    disabled: {type: Boolean, default: false},
    required: {type: Boolean, default: false},
    rules: {type: Array, required: true},
    on: {type: Object, required: true},
    modelRoot: {type: Object}
  },
  data() {
    return {
      vuexWorkMode: false,
      url: null,
      urlKeys: [],
      appending: false,
      currentPage: 0,
      localItems: [],
      localPageable: null,
      loading: false,
      lazy: false,
      responseStructure: {
        dataReference: null,
        totalElementsReference: null
      },
      search: "",
      queryMode: false,
      queryParam: null,

      filterArray: [],
    };
  },
  watch: {
    search(val) {
      if (this.queryMode) {
        if ((!this.search || this.search.length >= 1)) {
          if (val !== null && this.localModel === null) { // localModel check to prevent before request where query == selected item
            this.debounceDataLoaded(val);
          }
        }
      }
    },
  },
  computed: {
    ...mapGetters([
      "getItems",
      "getTotalElements",
      "getLastDownloadedPage"
    ]),

    // FOR KEEP LIB EXPR PATTERN TO RESOLVE DEPENDENCY VALUES
    expDataFakeWrapper() {
      return this.modelRoot !== null ? {parent: {value: this.modelRoot}} : null
    },

    localModel: {
      get() {
        return this.value;
      },
      set(val) {
        if (val !== null) {
          if (this.schema.properties !== undefined) {
            let keys = Object.keys(this.schema.properties)
            let obj = {}
            keys.forEach(key => {
              if (key.split(".").length > 1) {
                let keyArray = key.split(".")
                obj[keyArray[0]] = {}
                obj[keyArray[0]][keyArray[1]] = val[keyArray[0]][keyArray[1]]
              } else {
                obj[key] = val[key]
              }
            })
            this.$emit('input', {key: this.fullKey, value: obj})
          } else {
            this.$emit('input', {key: this.fullKey, value: val})
          }
        } else {
          this.$emit('input', {key: this.fullKey, value: null})
        }
      },
    },

    localRules() {
      if (this.required) {
        return [value => !!value || this.$t('required'), ...this.rules]
      } else {
        return this.rules
      }
    },

    localProps() {
      const global = this.options['fieldProps']
      const textField = this.options['comboboxProps'] !== undefined && this.options['comboboxProps'] !== null ? this.options['comboboxProps'] : {}
      const forThis = this.schema['x-props'] !== undefined && this.schema['x-props'] !== null ? this.schema['x-props'] : {}
      let result = global !== undefined && global !== null ? global : {}
      result = {...result, ...textField}
      result = {...result, ...forThis}

      if (this.schema["readOnly"]) {
        result = {...result, ...{outlined: false}}
      }

      return result
    },

    isNextPage() {
      return this.localPageable.isNextPage();
    },

    returnLocalItems() {
      return this.localItems
    }
  },
  methods: {
    ...mapActions([
      "setItems",
      "setTotalElements",
      "setLastDownloadedPage"
    ]),

    getQueryUrlParam() {
      if (this.queryMode && this.search !== null) {
        return `&${this.queryParam}=${this.search}`
      }
    },

    getFilterUrlParam() {
      let filter = ""
      if (this.filterArray.length > 0) {
        filter += "&filter="

        let iterator = new Iterator(this.filterArray)
        for (let filterElement = iterator.first(); iterator.hasNext();) {
          filter += filterElement.selector
          filter += encodeURIComponent(filterElement.operator)
          if ("value" in filterElement) {
            let value = this.getValueFromExpr(filterElement.value)
            if (value !== undefined) {
              filter += value
            } else {
              filter += filterElement.value
            }
          }
          filterElement = iterator.next()
          if (iterator.hasNext()) {
            filter += ";"
          }
        }
      }
      return filter
    },

    getValueFromExpr(exp) {
      return exprEvalParser.evaluate(exp, this.expDataFakeWrapper);
    },

    getPaginationParams(vuexWorkMode = false, loadMore = false) {
      const urlHasParameters = this.url.includes("?")
      if (vuexWorkMode) {
        return urlHasParameters ?
            `&page=${this.getLastDownloadedPage + 1}&size=${this.localPageable.getItemsPerPage()}`
            :
            `?page=${this.getLastDownloadedPage + 1}&size=${this.localPageable.getItemsPerPage()}`
      } else if (loadMore) {
        return urlHasParameters ?
            `&page=${this.localPageable.getPage() + 1}&size=${this.localPageable.getItemsPerPage()}`
            :
            `?page=${this.localPageable.getPage() + 1}&size=${this.localPageable.getItemsPerPage()}`
      } else {
        return urlHasParameters ?
            `&page=${this.localPageable.getPage()}&size=${this.localPageable.getItemsPerPage()}`
            :
            `?page=${this.localPageable.getPage()}&size=${this.localPageable.getItemsPerPage()}`
      }
    },

    fillUrlDependencyParams() {
      if (this.urlKeys.length > 0) {
        for (const key of this.urlKeys) {
          let value = this.getValueFromExpr(key)
          this.url = this.url.replace(`{${key}}`, value)
        }
      }
    },

    load() {
      this.loading = true
      this.localPageable.resetPage()

      if (this.vuexWorkMode && this.getItems.length > 0) {
        this.localItems = this.getItems
        this.localPageable.setTotalElements(this.getTotalElements)
        this.loading = false
      } else {

        const paginationUrlParams = this.getPaginationParams()
        const filterParam = this.getFilterUrlParam()

        // Fill url dependency parent variables
        this.fillUrlDependencyParams()

        this.options.httpLib.get(`${this.url}${paginationUrlParams}${filterParam}`)
            .then((response) => {
              let data = response.data
              this.checkResponseStructure(data)
              this.updateLocalItems(data, false)
              this.updateLocalPageable(data)
              //STORE SETUP TO PREVENT UNNECESSARY REQUEST ON ADD NEW ROW
              if (this.vuexWorkMode) {
                this.updateVuexState()
              }
            })
            .catch((error) => {
              console.log(error)
            })
            .finally(() => {
              this.loading = false
            })
      }
    },

    loadMore(entries, observer, isIntersecting) {
      this.appending = isIntersecting;
      if (isIntersecting && this.localPageable.isNextPage()) {

        const queryUrlParam = this.getQueryUrlParam()
        const filterParam = this.getFilterUrlParam()
        const paginationUrlParams = this.getPaginationParams(this.vuexWorkMode, true)

        this.options.httpLib.get(`${this.url}${paginationUrlParams}${queryUrlParam}${filterParam}`)
            .then((response) => {
              this.localPageable.nextPage()
              let data = response.data
              this.checkResponseStructure(data)
              this.updateLocalItems(data, true)
              this.updateLocalPageable(data)
              //STORE SETUP TO PREVENT UNNECESSARY REQUEST ON ADD NEW ROW
              if (this.vuexWorkMode) {
                console.log("Wchodze w vuex update")
                this.updateVuexState()
              }
            })
            .catch((error) => {
              console.log(error)
            })
            .finally(() => {
              this.loading = false
            })
      }
    },

    debounceDataLoaded: _.debounce(function (val) {
      let queryUrlParam = `&${this.queryParam}=${val}`
      let paginationUrlParams = `?page=${0}&size=${this.localPageable.getItemsPerPage()}`
      const filterParam = this.getFilterUrlParam()
      // URL contain filled params from URL exp variables, because load() function did it :)
      this.options.httpLib.get(`${this.url}${paginationUrlParams}${queryUrlParam}${filterParam}`)
          .then((response) => {
            this.localPageable.nextPage()
            let data = response.data
            this.checkResponseStructure(data)
            this.updateLocalItems(data, false)
            this.updateLocalPageable(data)
          })
          .catch((error) => {
            console.log(error)
          })
          .finally(() => {
            this.loading = false
          })
    }, 200),

    updateLocalItems(data, isLoadMore) {
      let dataReferenceArray = this.responseStructure.dataReference.split(".")
      if (dataReferenceArray.length > 1) {
        if (isLoadMore) {
          this.localItems = this.localItems.concat(data[dataReferenceArray[0]][dataReferenceArray[1]])
        } else {
          this.localItems = data[dataReferenceArray[0]][dataReferenceArray[1]]
        }
      } else {
        if (isLoadMore) {
          this.localItems = this.localItems.concat(data[this.responseStructure.dataReference])
        } else {
          this.localItems = data[this.responseStructure.dataReference]
        }
      }
    },

    updateLocalPageable(data) {
      let arr = this.responseStructure.totalElementsReference.split(".")
      if (arr.length > 1) {
        this.localPageable.setTotalElements(data[arr[0]][arr[1]])
      } else {
        this.localPageable.setTotalElements(data[arr[0]])
      }
    },

    updateVuexState() {
      this.setItems(this.localItems)
      this.setTotalElements(this.localPageable.getTotalElements())
      this.setLastDownloadedPage(this.localPageable.getPage())
    },

    checkResponseStructure(data) {
      let dataReferenceArray = this.responseStructure.dataReference.split(".")
      if (dataReferenceArray.length > 1) {
        if (!(dataReferenceArray[0] in data && dataReferenceArray[1] in data[dataReferenceArray[0]])) {
          console.log(`W odpowiedzi żądania brak podanej referencji na dane =  ${this.responseStructure.dataReference}`)
        } else {
          if (!(dataReferenceArray[0] in data)) {
            console.log(`W odpowiedzi żądania brak podanej referencji na dane = ${this.responseStructure.dataReference}`)
          }
        }
      }

      let totalElReferenceArray = this.responseStructure.totalElementsReference.split(".")
      if (totalElReferenceArray.length > 1) {
        if (!(totalElReferenceArray[0] in data && totalElReferenceArray[1] in data[totalElReferenceArray[0]])) {
          console.log(`W odpowiedzi żądania brak podanej referencji na totalElements =  ${this.responseStructure.totalElementsReference}`)
        }
      } else {
        if (!(totalElReferenceArray[0] in data)) {
          console.log(`W odpowiedzi żądania brak podanej referencji na totalElements =  ${this.responseStructure.totalElementsReference}`)
        }
      }
    },

    initBaseProperties() {
      if (this.fullSchema["x-source"] === undefined) console.log("Brak podanego źródła danych do pobierania elementów. Poprawny parametr to 'x-source'. Sprawdź schemat")
      if (this.url === null) this.url = this.schema['x-source']

      if (this.fullSchema["response-structure"] !== undefined) {
        this.responseStructure = {
          dataReference: this.fullSchema["response-structure"].dataReference !== undefined ?
              this.fullSchema["response-structure"].dataReference : "content",
          totalElementsReference: this.fullSchema['response-structure'].totalElementsReference !== undefined ?
              this.fullSchema['response-structure'].totalElementsReference : "totalElements"
        }
      } else {
        this.responseStructure = {
          dataReference: "content",
          totalElementsReference: "totalElements"
        }
      }
    },

    initPageable() {
      this.lazy = this.fullSchema['pageable-config'] !== undefined ?
          (this.fullSchema['pageable-config']['lazy'] !== undefined ? this.fullSchema['pageable-config']['lazy'] : false)
          : false

      if (this.fullSchema['pageable-config'] !== undefined && this.fullSchema['pageable-config'].itemPerPage !== undefined) {
        this.localPageable = new Pagination(this.fullSchema['pageable-config'].itemPerPage)
      } else {
        this.localPageable = new Pagination(20)
      }
    },

    initSearchable() {
      if (this.fullSchema['searchable-config'] !== undefined) {
        this.queryMode = this.fullSchema['searchable-config'].queryMode
        this.queryParam = this.fullSchema['searchable-config'].queryParam
      } else {
        this.queryMode = false
      }
    },

    initConfig() {
      this.initBaseProperties()
      this.initPageable()
      this.initSearchable()
      this.load()
    },

    initUrlMode() {
      if (this.schema['x-filter'] !== undefined) {
        this.filterArray = this.schema['x-filter']
      }
      this.urlKeys = matchAll(this.schema['x-source'], /\{(.*?)\}/g).toArray().filter(key => key !== 'q')
      this.initConfig()
    },

    printConfig() {
      console.log("Konriguracja kontrolki o kluczu: ", this.fullKey)
      console.log("this.fullSchema", this.fullSchema)
      console.log("this.schema", this.schema)
      console.log("Tryb VUEX", this.vuexWorkMode ? "Włączony" : "Wyłączony")
      console.log("Aktualna wartość: ", this.localModel)
      console.log("Źródło danych", this.schema["x-source"])
      console.log("Wyświetlany prop", this.schema["x-itemTitle"])
      console.log("Klucz prop", this.schema['x-itemKey'])

      if ('properties' in this.schema) {
        console.log("Struktura zapisywana do modelu", this.schema.properties)
      } else {
        console.log("Brak zdefiniowanej struktury - zapisuje sie cala struktura")
      }

      if (this.lazy) {
        console.log("Paginacja włączona")
        console.log("Item per page", this.localPageable.getItemsPerPage())
      } else {
        console.log("Paginacja wyłączona")
      }

      if (this.queryMode) {
        console.log("Szukajka włączona")
      } else {
        console.log("Szukajka wyłączona")
      }
    }

  },
  mounted() {
    // URL lub EXP
    if (!isValidUrl(this.schema['x-source'])) { // Source = EXP

      let result = this.getValueFromExpr(this.schema["x-source"]) // Variable should be typeof ARRAY or STRING (URL)

      if (result instanceof String && isValidUrl(result)) { // EXP variable is a URL (available pageable + searchable)
        this.url = result
        this.initUrlMode()
      }

      if (result instanceof Array) { // EXP variable is array (unavailable pageable + searchable, because all items are in result)
        this.lazy = false
        this.queryMode = false
        this.vuexWorkMode = false
        this.localItems = result
      }

    } else { // Source = URL
      this.initUrlMode()
    }

    // CHECK IF INCOMING VALUE IS NULL OR EMPTY OBJECT
    if (this.value !== null && this.localModel !== null && Object.keys(this.value).length === 0 && Object.keys(this.localModel).length === 0) {
      this.localModel = null
    }
  }
};
</script>

<i18n lang="i18n">
{
  "en": {
    "required": "This information is required. - local"
  },
  "pl": {
    "required": "Pole jest wymagane. - local"
  }
}
</i18n>

<style></style>
